import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { IMaterialToAccount } from '../../../sp-types/accountTypes';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (_percentages: number[], _isPercent: boolean) => void;
  accountMaterial?: IMaterialToAccount;
  strategy: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Default pattern: 100%, 100%, 100%, 75%, 75%, 75%, 50%, 50%, 50%, 25%, 25%, 25%
const DEFAULT_PERCENTAGES = [100, 100, 100, 75, 75, 75, 50, 50, 50, 25, 25, 25];

export const EditStrategyDialog: React.FC<Props> = ({ open, onClose, onApply, accountMaterial, strategy }) => {
  const [editBy, setEditBy] = useState<number>(
    strategy === 'logistics'
      ? accountMaterial?.isPercentStrategy
        ? 1
        : 0
      : strategy === 'coverage'
      ? accountMaterial?.covRatioIsPercent
        ? 1
        : 0
      : 0,
  ); // 0 = Value, 1 = Percentage
  const [percentages, setPercentages] = useState<number[]>(
    (strategy === 'logistics'
      ? accountMaterial?.logisticsStrategy
      : strategy === 'coverage'
      ? accountMaterial?.covRatioStrategy
      : null) || DEFAULT_PERCENTAGES,
  );
  const [requiredPeriod, setRequiredPeriod] = useState<number>(12);
  const [bufferAmount, setBufferAmount] = useState<number>(60);

  React.useEffect(() => {
    setEditBy(
      strategy === 'logistics'
        ? accountMaterial?.isPercentStrategy
          ? 1
          : 0
        : strategy === 'coverage'
        ? accountMaterial?.covRatioIsPercent
          ? 1
          : 0
        : 0,
    ); // 0 = Value

    setPercentages(
      (strategy === 'logistics'
        ? accountMaterial?.logisticsStrategy
        : strategy === 'coverage'
        ? accountMaterial?.covRatioStrategy
        : null) || DEFAULT_PERCENTAGES,
    );

    setBufferAmount(accountMaterial?.bufferStrategy || 60);
  }, [accountMaterial, strategy]);

  const editByChips = [
    { key: 'Value', value: 0 },
    { key: 'Percentage', value: 1 },
  ];

  const handlePercentageChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const newPercentages = [...percentages];
    newPercentages[index] = numValue;
    setPercentages(newPercentages);
  };

  const handleApply = () => {
    if (strategy === 'buffer') {
      onApply([bufferAmount], false);
    } else {
      onApply(percentages, editBy === 1);
    }
    onClose();
  };

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const currMonth = new Date().getMonth();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <span>Edit {strategy} strategy</span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-2">
          {strategy === 'buffer' ? (
            /* Buffer strategy UI */
            <div className="flex flex-col gap-4">
              <div className="text-sm text-gray-600 mb-2">Buffer Amount</div>
              <div className="flex items-center gap-2">
                <TextField
                  type="number"
                  value={bufferAmount}
                  onChange={(e) => setBufferAmount(parseInt(e.target.value) || 60)}
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{ width: '120px' }}
                  label="Buffer Amount"
                />
              </div>
            </div>
          ) : (
            /* Logistics and Coverage strategy UI */
            <>
              {/* Edit by toggle */}
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600 mb-2">Edit by</div>
                <div className="flex">
                  <ToggleChips chips={editByChips} selected={editBy} setSelected={setEditBy} width="6rem" />
                </div>
              </div>

              {/* Required period */}
              <div>
                <div className="text-sm text-gray-600 mb-2">Required period</div>
                <div className="flex items-center gap-2">
                  <TextField
                    type="number"
                    value={requiredPeriod}
                    onChange={(e) => setRequiredPeriod(parseInt(e.target.value) || 12)}
                    size="small"
                    inputProps={{ min: 1, max: 12 }}
                    sx={{ width: '80px' }}
                  />
                  <span className="text-sm">months</span>
                </div>
              </div>

              {/* Required coverage table */}
              <div>
                <div className="text-sm text-gray-600 mb-2">Required coverage</div>
                <div className="text-center text-sm font-medium mb-2">
                  {currentYear}-{nextYear}
                </div>

                {/* Month headers */}
                <div className="grid grid-cols-12 gap-2 mb-2">
                  {Array.from({ length: 12 }).map((_month, index) => (
                    <div key={index} className="text-center text-xs text-gray-600 font-medium">
                      {MONTH_NAMES[(currMonth + index) % 12]}
                    </div>
                  ))}
                </div>

                {/* Percentage inputs */}
                <div className="grid grid-cols-12 gap-2">
                  {percentages.slice(0, 12).map((percentage, index) => (
                    <TextField
                      key={index}
                      type="number"
                      value={percentage}
                      onChange={(e) => handlePercentageChange(index, e.target.value)}
                      size="small"
                      inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                      InputProps={{
                        endAdornment: editBy === 1 ? <span style={{ fontSize: '0.75rem' }}>%</span> : '',
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: '4px 2px 4px 0',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
